import { supabase } from './supabaseClient';

export type PipelineStage =
  | 'sourced'
  | 'contacted'
  | 'applied'
  | 'screening'
  | 'interview'
  | 'offer'
  | 'hired'
  | 'archived';

export type TransitionTrigger =
  | 'messaging'
  | 'manual'
  | 'system'
  | 'prescreen'
  | 'interview_confirm'
  | 'offer'
  | 'hire';

// Canonical forward order — archived is a side-exit, not a forward stage.
const STAGE_ORDER: PipelineStage[] = [
  'sourced',
  'contacted',
  'applied',
  'screening',
  'interview',
  'offer',
  'hired',
];

// Stages that block further transitions (terminal).
const TERMINAL_STAGES: PipelineStage[] = ['hired'];

// Returns true when newStage is a valid forward move from currentStage.
// archived is always allowed as a side-exit.
// Any forward move in STAGE_ORDER is allowed (not just adjacent hops),
// matching the spec: "latest valid recruiter action ALWAYS overrides previous".
// Backwards moves are rejected.
function isValidTransition(from: PipelineStage | null, to: PipelineStage): boolean {
  if (to === 'archived') return true;
  if (from === 'archived' && to === 'sourced') return true; // re-adding re-activates
  if (from === 'archived') return false;
  if (from && TERMINAL_STAGES.includes(from)) return false;

  const fromIdx = from ? STAGE_ORDER.indexOf(from) : -1;
  const toIdx = STAGE_ORDER.indexOf(to);

  if (toIdx === -1) return false; // unknown stage
  return toIdx > fromIdx; // forward only
}

export interface TransitionOptions {
  clientId: string;
  consultantId: string;
  jobPostingId?: string | null;
  newStage: PipelineStage;
  triggeredBy?: TransitionTrigger;
  messageId?: string | null;
}

export interface TransitionResult {
  success: boolean;
  entryId?: string;
  previousStage?: PipelineStage | null;
  error?: string;
}

/**
 * Single authoritative function for all pipeline stage changes.
 *
 * Behaviour:
 * - If no pipeline entry exists: creates one at newStage (upsert).
 * - If entry exists: validates the transition is forward, then updates.
 * - Writes an audit row to pipeline_stage_transitions.
 * - Returns the entry id and previous stage for callers that need them.
 */
export async function transitionCandidateStage(opts: TransitionOptions): Promise<TransitionResult> {
  const { clientId, consultantId, jobPostingId = null, newStage, triggeredBy = 'manual', messageId = null } = opts;

  try {
    // ── 1. Fetch existing entry (null if none) ────────────────────────────────
    let query = supabase
      .from('candidate_pipeline_entries')
      .select('id, stage')
      .eq('client_id', clientId)
      .eq('consultant_id', consultantId);

    // Match on job_posting_id — null means "no specific posting" which is a valid bucket.
    if (jobPostingId) {
      query = query.eq('job_posting_id', jobPostingId);
    } else {
      query = query.is('job_posting_id', null);
    }

    const { data: existing, error: fetchErr } = await query.maybeSingle();
    if (fetchErr) return { success: false, error: fetchErr.message };

    const previousStage = (existing?.stage as PipelineStage | null) ?? null;

    // ── 2. Validate transition ────────────────────────────────────────────────
    if (!isValidTransition(previousStage, newStage)) {
      return {
        success: false,
        error: `Invalid transition: ${previousStage ?? 'none'} → ${newStage}`,
      };
    }

    // ── 3. Upsert pipeline entry ──────────────────────────────────────────────
    let entryId: string;

    if (existing) {
      const { error: updateErr } = await supabase
        .from('candidate_pipeline_entries')
        .update({ stage: newStage, updated_at: new Date().toISOString() })
        .eq('id', existing.id);
      if (updateErr) return { success: false, error: updateErr.message };
      entryId = existing.id;
    } else {
      const { data: inserted, error: insertErr } = await supabase
        .from('candidate_pipeline_entries')
        .insert({
          client_id: clientId,
          consultant_id: consultantId,
          job_posting_id: jobPostingId,
          stage: newStage,
          notes: '',
          is_favorited: false,
        })
        .select('id')
        .single();
      if (insertErr) return { success: false, error: insertErr.message };
      entryId = inserted.id;
    }

    // ── 4. Write audit row ────────────────────────────────────────────────────
    await supabase.from('pipeline_stage_transitions').insert({
      pipeline_entry_id: entryId,
      client_id: clientId,
      consultant_id: consultantId,
      from_stage: previousStage,
      to_stage: newStage,
      triggered_by: triggeredBy,
      message_id: messageId,
    });

    return { success: true, entryId, previousStage };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}
